import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { getRedirectPath, useRentReportingAuth } from '../contexts/RentReportingAuthProvider';
import type { ClickstreamAuthTriggerSource } from '../analytics/events';
import { emitAuthCompleteEvent } from '../analytics/events';
import { EnrollmentProvider, ReportingForAllEnrollmentStatus } from '@zg-rentals/ts-rent-credit-report-client';

interface EnrollmentRedirectHandlerProps {
  children: React.ReactNode;
}

export const EnrollmentRedirectHandler: React.FC<EnrollmentRedirectHandlerProps> = ({ children }) => {
  const router = useRouter();
  const { user, enrollmentMutation } = useRentReportingAuth();

  // Initialize state based on URL params to prevent flash
  const [isHandlingPostLogin, setIsHandlingPostLogin] = useState(() => {
    // Check if we're handling a post-login action on initial render
    if (typeof window === 'undefined') return false;
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('post_login_action') && urlParams.has('auth_trigger');
  });
  const isProcessingPostLogin = useRef(false);
  const hasEmittedAuthComplete = useRef<string | null>(null);

  useEffect(() => {
    const cleanUpUrlParams = (clearStep = false) => {
      const {
        auth_trigger: _auth_trigger,
        post_login_action: _post_login_action,
        step: _step,
        ...restQuery
      } = router.query;
      router.replace(
        {
          pathname: router.pathname,
          query: clearStep ? restQuery : { ...restQuery, ...(router.query.step && { step: router.query.step }) },
        },
        undefined,
        { shallow: true },
      );
    };

    const enrollmentRedirect = async () => {
      if (user === null) {
        return;
      }

      // Check if auth was just completed by looking for auth_trigger query param
      const authTrigger = router.query.auth_trigger as string | undefined;
      const postLoginAction = router.query.post_login_action as string | undefined;

      // Prevent re-entry while async post-login operations are in progress
      if (isProcessingPostLogin.current) {
        return;
      }

      // Reset state if there's no post-login action in the URL or if user has already initiated enrollment
      if (!postLoginAction || user.hasInitiatedEnrollment) {
        setIsHandlingPostLogin(false);
        isProcessingPostLogin.current = false;
      }

      if (authTrigger && user.isLoggedIn) {
        // Only emit event once per auth_trigger value
        if (hasEmittedAuthComplete.current !== authTrigger) {
          emitAuthCompleteEvent(authTrigger as ClickstreamAuthTriggerSource);
          hasEmittedAuthComplete.current = authTrigger;
        }
      }

      // Handle post-login actions
      if (
        postLoginAction === 'initiate_enrollment' &&
        !user.hasInitiatedEnrollment &&
        !user.hasCompletedEnrollment &&
        !enrollmentMutation.isPending &&
        !isProcessingPostLogin.current
      ) {
        isProcessingPostLogin.current = true;
        setIsHandlingPostLogin(true);
        try {
          await enrollmentMutation.mutateAsync({
            postData: {
              enrollmentProvider: EnrollmentProvider.ESUSU,
              enrollmentStatus: ReportingForAllEnrollmentStatus.INITIATED_ENROLLMENT,
            },
          });
          cleanUpUrlParams(true); // Clear step param after successful enrollment initiation
          setIsHandlingPostLogin(false);
          isProcessingPostLogin.current = false;
        } catch (error) {
          const { auth_trigger: _auth_trigger, post_login_action: _post_login_action, ...restQuery } = router.query;
          await router.replace({
            pathname: '/enroll',
            query: { ...restQuery, enrollment_error: 'true', step: 'privacyNotice' },
          });
        }
        return;
      }

      // Only clear auth-related params when there's auth but no post-login action
      // This handles cases like request payments modal where we want to preserve step
      if (!postLoginAction && (authTrigger || postLoginAction)) {
        cleanUpUrlParams(false);
      }

      // Skip redirect logic if we just had an enrollment error (even after param is cleaned up)
      const hasEnrollmentError = router.query.enrollment_error === 'true';
      if (!hasEnrollmentError) {
        const currentPath = router.pathname;
        const queryParams = router.query;
        const redirectPath = getRedirectPath(currentPath, queryParams as Record<string, string>, user);
        if (redirectPath) {
          const fromApp = router.query.fromApp;
          if (fromApp) {
            router.replace({ pathname: redirectPath, query: { fromApp } });
          } else {
            router.replace(redirectPath);
          }
        }
      }
    };

    enrollmentRedirect();
    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, router.query, router.pathname]);

  // Don't render children while handling post-login action to prevent flash
  if (isHandlingPostLogin) {
    return null;
  }

  return <>{children}</>;
};
